import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  FormControl,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

function AdvertiserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [advertiser, setAdvertiser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState('active');
  const [stats, setStats] = useState({ clicks: 0, conversions: 0, revenue: 0, daily: [] });
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [endDate, setEndDate] = useState(new Date());
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    name: '', company: '', email: '', phone: '', status: '',
    billing_company: '', billing_email: '', billing_address: '',
    billing_vat: '', billing_sdi: '', payment_terms: '', notes: ''
  });

  useEffect(() => {
    fetchAdvertiser();
    fetchCampaigns();
  }, [id]);

  useEffect(() => {
    fetchCampaigns();
  }, [campaignFilter]);

  useEffect(() => {
    if (tabValue === 2) {
      fetchStats();
    }
  }, [tabValue, startDate, endDate]);

  const fetchAdvertiser = async () => {
    try {
      const response = await axios.get(`/admin/advertisers/${id}`);
      const data = response.data.data;
      setAdvertiser(data);
      setFormData({
        name: data.name || '',
        company: data.company || '',
        email: data.email || '',
        phone: data.phone || '',
        status: data.status || '',
        billing_company: data.billing_company || '',
        billing_email: data.billing_email || '',
        billing_address: data.billing_address || '',
        billing_vat: data.billing_vat || '',
        billing_sdi: data.billing_sdi || '',
        payment_terms: data.payment_terms || '',
        notes: data.notes || ''
      });
    } catch (error) {
      console.error('Error fetching advertiser:', error);
      setMessage({ type: 'error', text: 'Failed to load advertiser' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`/admin/advertisers/${id}/campaigns?status=${campaignFilter}`);
      setCampaigns(response.data.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`/admin/advertisers/${id}/stats`, {
        params: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        }
      });
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/admin/advertisers/${id}`, formData);
      setAdvertiser({ ...advertiser, ...formData });
      setEditMode(false);
      setMessage({ type: 'success', text: 'Advertiser saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save advertiser' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setFormData({
      name: advertiser.name || '',
      company: advertiser.company || '',
      email: advertiser.email || '',
      phone: advertiser.phone || '',
      status: advertiser.status || '',
      billing_company: advertiser.billing_company || '',
      billing_email: advertiser.billing_email || '',
      billing_address: advertiser.billing_address || '',
      billing_vat: advertiser.billing_vat || '',
      billing_sdi: advertiser.billing_sdi || '',
      payment_terms: advertiser.payment_terms || '',
      notes: advertiser.notes || ''
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!advertiser) {
    return (
      <Box>
        <Typography color="error">Advertiser not found</Typography>
        <Button onClick={() => navigate('/advertisers')}>Back to Advertisers</Button>
      </Box>
    );
  }

  const tabs = ['General', 'Campaigns', 'Stats'];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/advertisers')}>Back</Button>
          <Typography variant="h5" fontWeight={700} flexGrow={1}>{advertiser.name}</Typography>
          <Chip 
            label={advertiser.status === 'active' ? 'Active' : 'Inactive'} 
            color={advertiser.status === 'active' ? 'success' : 'error'} 
            sx={{ color: 'white' }}
          />
        </Box>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
            {tabs.map((label, i) => <Tab key={i} label={label} />)}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Card>
          <CardContent>
            {/* General Tab */}
            {tabValue === 0 && (
              <Box>
                {/* Header with Edit button */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Advertiser Information</Typography>
                  {!editMode ? (
                    <Button startIcon={<Edit />} variant="outlined" onClick={() => setEditMode(true)}>Edit</Button>
                  ) : (
                    <Box display="flex" gap={1}>
                      <Button startIcon={<Save />} variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button startIcon={<Cancel />} variant="outlined" onClick={handleCancelEdit}>Cancel</Button>
                    </Box>
                  )}
                </Box>

                {/* Section: Basic Info */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={600} mb={2}>Basic Information</Typography>
                  <Box display="flex" gap={4} flexWrap="wrap">
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Name</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} sx={{ mt: 0.5 }} />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.name || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Company</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} sx={{ mt: 0.5 }} />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.company || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Email</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} sx={{ mt: 0.5 }} />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.email || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Phone</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} sx={{ mt: 0.5 }} />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.phone || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 15%', minWidth: 100 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Status</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.status || '-'}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 20%', minWidth: 150 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Created At</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                        {advertiser.created_at ? new Date(advertiser.created_at).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Section: Billing Information */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={600} mb={2}>Billing Information</Typography>
                  <Box display="flex" gap={4} flexWrap="wrap">
                    <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Billing Company</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.billing_company} onChange={e => setFormData({...formData, billing_company: e.target.value})} sx={{ mt: 0.5 }} placeholder="Company name for invoices" />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.billing_company || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Billing Email</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.billing_email} onChange={e => setFormData({...formData, billing_email: e.target.value})} sx={{ mt: 0.5 }} placeholder="billing@company.com" />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.billing_email || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 100%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Billing Address</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.billing_address} onChange={e => setFormData({...formData, billing_address: e.target.value})} sx={{ mt: 0.5 }} placeholder="Full address for invoices" multiline rows={2} />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{advertiser.billing_address || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 150 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>VAT Number</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.billing_vat} onChange={e => setFormData({...formData, billing_vat: e.target.value})} sx={{ mt: 0.5 }} placeholder="IT12345678901" />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.billing_vat || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 150 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>SDI Code</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.billing_sdi} onChange={e => setFormData({...formData, billing_sdi: e.target.value})} sx={{ mt: 0.5 }} placeholder="0000000" />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.billing_sdi || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 150 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Payment Terms</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.payment_terms} onChange={e => setFormData({...formData, payment_terms: e.target.value})} sx={{ mt: 0.5 }} placeholder="Net 30, Net 60..." />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{advertiser.payment_terms || '-'}</Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>

                {/* Section: Notes */}
                <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#fffde7', borderColor: '#ffe082' }}>
                  <Typography variant="subtitle2" color="warning.dark" fontWeight={600} mb={2}>Notes</Typography>
                  {editMode ? (
                    <TextField fullWidth multiline rows={4} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Internal notes about this advertiser..." />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: advertiser.notes ? 'inherit' : 'text.secondary' }}>
                      {advertiser.notes || 'No notes'}
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}

            {/* Campaigns Tab */}
            {tabValue === 1 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Campaigns</Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="deleted">Deleted</MenuItem>
                      <MenuItem value="all">All</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Payout</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaigns.length > 0 ? campaigns.map((campaign) => (
                        <TableRow 
                          key={campaign.campaign_id} 
                          hover 
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/campaigns/${campaign.campaign_id}`)}
                        >
                          <TableCell>{campaign.campaign_id}</TableCell>
                          <TableCell>
                            <Typography color="primary.main" fontWeight={500}>
                              {campaign.name}
                            </Typography>
                          </TableCell>
                          <TableCell>€{parseFloat(campaign.payout || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={campaign.status} 
                              color={campaign.status === 'active' ? 'success' : 'default'} 
                              size="small"
                              sx={{ color: 'white' }}
                            />
                          </TableCell>
                          <TableCell>
                            {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="textSecondary">No campaigns found</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Stats Tab */}
            {tabValue === 2 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Statistics</Typography>
                  <Box display="flex" gap={2}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </Box>
                </Box>
                
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
                      <Typography color="textSecondary" variant="body2" gutterBottom>Clicks</Typography>
                      <Typography variant="h4" fontWeight={600}>{stats.clicks}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
                      <Typography color="textSecondary" variant="body2" gutterBottom>Conversions</Typography>
                      <Typography variant="h4" fontWeight={600}>{stats.conversions}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
                      <Typography color="textSecondary" variant="body2" gutterBottom>Revenue</Typography>
                      <Typography variant="h4" fontWeight={600} color="success.main">€{stats.revenue.toFixed(2)}</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {stats.daily && stats.daily.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Conversions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.daily.map((day) => (
                          <TableRow key={day.date}>
                            <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                            <TableCell align="right">{day.conversions}</TableCell>
                            <TableCell align="right">€{parseFloat(day.revenue).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
}

export default AdvertiserDetails;
